/**
 * Reine Logik des Graph-Explorers: Knoten und Kanten zusammenführen, Farben, Werte darstellen.
 *
 * Sie liegt außerhalb der Komponente, weil das Zusammenführen die einzige Stelle ist, an der beim
 * Erkunden etwas schiefgehen kann, das man nicht sieht: ein doppelt eingefügter Knoten sieht aus
 * wie zwei Objekte, ein verlorener wie ein fehlender Datensatz.
 */

export interface ExplorerNode {
  id: string;
  type: string;
  label: string;
  attributes: Record<string, any>;
  /** Vom Nutzer ausgeklappt — der Explorer zeigt das, damit man weiß, wo man schon war. */
  expanded?: boolean;
}

export interface ExplorerLink {
  id: string;
  source: string;
  target: string;
  relation: string;
  label: string;
  direction: "out" | "in";
}

export interface ExplorerGraph {
  nodes: ExplorerNode[];
  links: ExplorerLink[];
}

export const emptyGraph = (): ExplorerGraph => ({ nodes: [], links: [] });

/**
 * Neue Knoten und Kanten dazunehmen, **ohne** vorhandene zu ersetzen.
 *
 * Zwei Regeln, beide unangenehm, wenn man sie falsch macht: ein Knoten, der schon da ist, behält
 * seinen Platz (die Kraftsimulation würde ihn sonst bei jedem Ausklappen neu schleudern und die
 * Karte wäre unlesbar), und er behält seine Attribute — die Nachbarschaftsabfrage liefert ein
 * Objekt möglicherweise verkürzt, und ein vollständig geladener Knoten darf davon nichts verlieren.
 */
export const mergeGraph = (graph: ExplorerGraph, addition: Partial<ExplorerGraph>): ExplorerGraph => {
  const nodes = [...graph.nodes];
  const index = new Map(nodes.map((node, position) => [node.id, position]));
  for (const incoming of addition.nodes ?? []) {
    const existing = index.get(incoming.id);
    if (existing === undefined) {
      nodes.push(incoming);
      index.set(incoming.id, nodes.length - 1);
      continue;
    }
    // **Die Identität des bekannten Knotens bleibt.** Ein `{...known}` wäre ein NEUES Objekt, und
    // die Kanten der Kraftsimulation halten nach ihrer ersten Auflösung Referenzen auf das alte:
    // sie zeigten dann auf einen Knoten, der nicht mehr im Graphen steht, und die Verbindung
    // verschwindet. Gemessen beim Ausklappen des zweiten Knotens — die Kante dorthin brach genau
    // dann, wenn er in der Nachbarschaft des ersten schon vorkam. Deshalb wird hier an Ort und
    // Stelle geändert; die Bibliothek besitzt diese Objekte ohnehin (sie schreibt Position und
    // Geschwindigkeit hinein).
    const known = nodes[existing];
    if (
      Object.keys(incoming.attributes ?? {}).length > Object.keys(known.attributes ?? {}).length
    ) {
      // Mehr Attribute gewinnen; ein kürzeres Objekt darf ein vollständiges nicht überschreiben.
      known.attributes = incoming.attributes;
    }
    if (incoming.expanded) known.expanded = true;
  }

  const links = [...graph.links];
  const seen = new Set(links.map(link => link.id));
  for (const link of addition.links ?? []) {
    if (seen.has(link.id)) continue;
    seen.add(link.id);
    links.push(link);
  }

  return { nodes, links };
};

/**
 * Einen Knoten als ausgeklappt vermerken — **ohne** sein Objekt zu ersetzen, aus demselben Grund
 * wie in {@link mergeGraph}: eine Kante, die auf das alte Objekt zeigt, verlöre ihr Ziel.
 */
export const markExpanded = (graph: ExplorerGraph, nodeId: string): ExplorerGraph => {
  const node = graph.nodes.find(candidate => candidate.id === nodeId);
  if (node) node.expanded = true;
  return { nodes: [...graph.nodes], links: graph.links };
};

/**
 * Dunkler oder heller Grund.
 *
 * Der dunkle ist die Voreinstellung — Kugeln und Leuchten wirken dort am besten. Der helle ist für
 * Bildschirmfotos, Präsentationen und Druck: Dort frisst ein schwarzes Bild Toner und sieht neben
 * weißen Folien wie ein Loch aus. Er ist keine Umfärbung des Hintergrunds allein — dieselben Farben
 * wären auf Weiß zu blass —, sondern ein eigener Satz: gleicher Farbton, dunkler und satter.
 */
export type GraphTheme = "dark" | "light";

export const GRAPH_BACKGROUND: Record<GraphTheme, string> = {
  dark: "#05070d",
  light: "#f8fafc",
};

const hueOf = (text: string, factor: number): number => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * factor + text.charCodeAt(i)) % 360;
  return hash;
};

/**
 * Eine stabile Farbe je Typ — aus dem Namen gerechnet, nicht aus einer Tabelle.
 *
 * Eine Tabelle müsste ~110 Typen führen und wäre beim ersten neuen falsch. Der Farbton kommt aus
 * einer Prüfsumme des Namens, Sättigung und Helligkeit sind fest, damit alle Farben zueinander
 * passen und auf dunklem Grund lesbar bleiben.
 */
export const colorForType = (type: string, theme: GraphTheme = "dark"): string =>
  theme === "light" ? `hsl(${hueOf(type, 31)}, 65%, 42%)` : `hsl(${hueOf(type, 31)}, 70%, 60%)`;

/**
 * Die Farbe einer Kante — nach ihrer **Relation**, nicht nach ihrer Richtung.
 *
 * Die Richtung sagt der Pfeil; die Farbe soll die Frage beantworten, die man beim Debuggen wirklich
 * hat: welche Kanten hier sind dieselbe Beziehung? Derselbe Hash wie bei den Knoten, aber heller
 * und kräftiger — eine Linie ist ein Pixel breit und muss sich gegen den dunklen Grund behaupten,
 * eine Kugel hat Fläche.
 */
export const colorForRelation = (relation: string, theme: GraphTheme = "dark"): string =>
  theme === "light"
    ? `hsl(${hueOf(relation, 37)}, 70%, 40%)`
    : `hsl(${hueOf(relation, 37)}, 85%, 68%)`;

/**
 * Dieselbe Farbe, nur zurückgenommen.
 *
 * Beim Hervorheben wird nicht das Gemeinte greller, sondern der Rest leiser — sonst verschiebt
 * sich die ganze Bildhelligkeit und man sieht schlechter statt besser. Alle Farben hier sind
 * `hsl(...)`, also bleibt der Farbton stehen und nur Sättigung und Helligkeit fallen.
 */
export const dimmed = (color: string, theme: GraphTheme = "dark"): string =>
  color.replace(/hsl\(\s*(\d+)\s*,[^)]*\)/, (_match, hue) =>
    // Zurückgenommen heißt: nah am Grund — auf Hell also blass, nicht dunkel.
    theme === "light" ? `hsl(${hue}, 15%, 86%)` : `hsl(${hue}, 12%, 24%)`,
  );

/**
 * Wie gut ein Eintrag der Klassenauswahl zur Eingabe passt — 0 heißt: nicht anzeigen.
 *
 * Die Voreinstellung von `cmdk` ist eine Teilfolgensuche: Sie nimmt jeden Eintrag an, in dem die
 * getippten Zeichen **irgendwo in dieser Reihenfolge** vorkommen. „Lease" traf damit auch
 * „Wissensbasis (KnowledgeBase)" — K-now**l**-**e**-dg-**e**-B-**as**-**e** — und stellte
 * „Mietangebot (LeaseOffer)" vor das gesuchte „Mietverhältnis (Lease)". Eine Liste, die auf ein
 * genaues Wort die falsche Zeile nach oben legt, ist schlimmer als eine ungefilterte.
 *
 * Deshalb: **jedes** getippte Wort muss als zusammenhängende Zeichenkette vorkommen, und ein
 * Volltreffer auf einem Wort schlägt einen Wortanfang, der wiederum ein Vorkommen mitten im Wort
 * schlägt. Weil jedes Wort einzeln geprüft wird, ist die Reihenfolge egal — „Lease Miet" findet
 * dasselbe wie „Miet Lease".
 */
export const typeMatchScore = (value: string, search: string): number => {
  const needle = String(search ?? "").trim().toLowerCase();
  if (!needle) return 1;
  const haystack = String(value ?? "").toLowerCase();
  const words = needle.split(/\s+/).filter(Boolean);
  if (!words.every(word => haystack.includes(word))) return 0;

  const fragments = haystack.split(/\s+/).filter(Boolean);
  let score = 0.3;
  for (const word of words) {
    for (const fragment of fragments) {
      if (fragment === word) score = Math.max(score, 1);
      else if (fragment.startsWith(word)) score = Math.max(score, 0.7);
    }
  }
  return score;
};

export interface TypeLegendEntry {
  type: string;
  color: string;
  /** Knoten dieser Klasse im Bild. */
  count: number;
  /** Unterlagen an diesen Knoten zusammen — eigene und über die Dokumentenanalyse zugeordnete. */
  documents: number;
  /** An wie vielen der Knoten sie hängen. Zwei Unterlagen können an einem Objekt liegen. */
  withDocuments: number;
}

/**
 * Welche Klassen im Bild vorkommen, mit Farbe und Anzahl.
 *
 * Die Knotenlegende ist die, nach der man sucht: Sie beantwortet „ist der Mangel dabei?". Die
 * Kantenlegende darunter kann das nicht — dort steht der Rollenname beider Enden derselben
 * Beziehung nebeneinander (`assets` und `building`), was wie zwei Typen in Plural und Singular
 * aussieht, aber keiner ist.
 */
export const typeLegend = (nodes: ExplorerNode[], theme: GraphTheme = "dark"): TypeLegendEntry[] => {
  const byType = new Map<string, TypeLegendEntry>();
  for (const node of nodes) {
    let entry = byType.get(node.type);
    if (!entry) {
      entry = {
        type: node.type,
        color: colorForType(node.type, theme),
        count: 0,
        documents: 0,
        withDocuments: 0,
      };
      byType.set(node.type, entry);
    }
    entry.count++;
    // Dieselbe Zählung wie am Knoten selbst — eine zweite Rechenart ergäbe eine Zahl, die neben
    // dem Blatt am Knoten steht und ihm widerspricht.
    const badge = documentBadge(node.attributes);
    if (badge.total > 0) {
      entry.documents += badge.total;
      entry.withDocuments++;
    }
  }
  return Array.from(byType.values()).sort(
    (a, b) => b.count - a.count || a.type.localeCompare(b.type),
  );
};

/** Welche Relationen im Bild vorkommen, mit Farbe und Anzahl — die Legende. */
export const relationLegend = (
  links: ExplorerLink[],
  theme: GraphTheme = "dark",
): Array<{ relation: string; label: string; color: string; count: number }> => {
  const byRelation = new Map<string, { relation: string; label: string; color: string; count: number }>();
  for (const link of links) {
    const known = byRelation.get(link.relation);
    if (known) {
      known.count++;
      continue;
    }
    byRelation.set(link.relation, {
      relation: link.relation,
      label: link.label || link.relation,
      color: colorForRelation(link.relation, theme),
      count: 1,
    });
  }
  // `Array.from` statt Spread: das Portal kompiliert auf ES5, und ein gespreizter Iterator
  // verlangt dort `downlevelIteration`.
  return Array.from(byRelation.values()).sort(
    (a, b) => b.count - a.count || a.relation.localeCompare(b.relation),
  );
};

/**
 * Die Farbe des hervorgehobenen Knotens.
 *
 * **Nicht Weiß.** Reines Weiß löscht die Typfarbe aus und leuchtet zusammen mit dem Bloom so stark,
 * dass der Knoten zur Lampe wird und seine Nachbarn überstrahlt. Stattdessen derselbe Farbton,
 * heller und satter: er sticht heraus und bleibt erkennbar als das, was er ist.
 */
export const highlightColorForType = (type: string, theme: GraphTheme = "dark"): string =>
  // Auf Hell sticht heraus, was dunkler ist — die Umkehrung des Leuchtens auf Dunkel.
  theme === "light" ? `hsl(${hueOf(type, 31)}, 95%, 30%)` : `hsl(${hueOf(type, 31)}, 95%, 78%)`;

/** Der Typ eines Knotens aus seiner Id (`Lease:6600…`) — die Id ist zusammengesetzt und eindeutig. */
export const typeOfNodeId = (id: string): string => String(id).split(":")[0];

/** Die reine Objekt-Id ohne Typpräfix — für den Server, der beides getrennt bekommt. */
export const rawIdOfNodeId = (id: string): string => String(id).split(":").slice(1).join(":");

/**
 * Ein Attributwert für die Anzeige — kurz, aber nie erfunden.
 *
 * Objekte und Listen werden als JSON gezeigt und ab 400 Zeichen gekürzt, **mit** Kennzeichnung:
 * ein stillschweigend abgeschnittener Wert im Debug-Panel ist genau die Art Halbwahrheit, wegen der
 * man am Ende die falsche Stelle sucht.
 */
export const formatAttributeValue = (value: any): { text: string; truncated: boolean } => {
  if (value === null || value === undefined) return { text: "—", truncated: false };
  if (value instanceof Date) return { text: value.toISOString(), truncated: false };
  if (typeof value === "object") {
    const json = JSON.stringify(value, null, 2);
    return json.length > 400
      ? { text: `${json.slice(0, 400)}…`, truncated: true }
      : { text: json, truncated: false };
  }
  const text = String(value);
  return text.length > 400 ? { text: `${text.slice(0, 400)}…`, truncated: true } : { text, truncated: false };
};

/** Die Attribute eines Knotens in fester Reihenfolge: erst die des Typs, dann alles Übrige. */
export const orderedAttributes = (
  attributes: Record<string, any>,
  fields: Array<{ name: string; label?: string | null }> | string[],
): Array<{ name: string; label: string | null; value: any; declared: boolean }> => {
  // Beide Formen: eine bloße Reihenfolge (wie bisher) oder die Felder samt Beschriftung.
  const declaredFields = (fields as any[]).map(field =>
    typeof field === 'string' ? { name: field, label: null } : { name: field.name, label: field.label ?? null }
  );

  const declared = declaredFields
    .filter(field => field.name in attributes)
    .map(field => ({
      name: field.name,
      label: field.label ?? null,
      value: attributes[field.name],
      declared: true,
    }));

  const known = new Set(declaredFields.map(field => field.name));
  const rest = Object.keys(attributes)
    .filter(name => !known.has(name))
    .sort()
    // Undeklarierte Felder haben keine Beschriftung — sie stehen ja nicht im Modell.
    .map(name => ({ name, label: null, value: attributes[name], declared: false }));

  // Undeklarierte Felder stehen unten und sind als solche markiert — im Datenbestand liegen sie,
  // im Modell nicht, und genau das will man beim Debuggen sehen.
  return [...declared, ...rest];
};

/**
 * Wie ein Typ heißt — deutsch, wenn das Modell es sagt, sonst technisch.
 *
 * **Beides, nie nur eines:** Die Beschriftung liest man, den technischen Namen tippt man in die
 * Abfrage. Ein Explorer, der nur „Mietvertrag" zeigt, zwingt beim nächsten Filter zum Raten, wie
 * die Klasse wirklich heißt.
 */
export const typeCaption = (
  typeName: string,
  labels: Map<string, string> | Record<string, string> | undefined,
): { label: string; technical: string } => {
  const lookup =
    labels instanceof Map ? labels.get(typeName) : labels ? labels[typeName] : undefined;
  const label = lookup && lookup.trim() ? lookup.trim() : typeName;
  return { label, technical: typeName };
};

/** Wie lange nach einem Klick ein zweiter noch als Doppelklick zählt. */
export const DOUBLE_CLICK_MS = 350;

/**
 * Ob ein Klick der zweite auf denselben Knoten ist.
 *
 * `react-force-graph-3d` liefert nur `onNodeClick`, der Doppelklick wird also selbst erkannt. Er
 * muss **denselben** Knoten treffen: zwei schnelle Klicks auf verschiedene Knoten sind zwei
 * Auswahlen und dürfen nichts ausklappen.
 */
export const isDoubleClick = (
  last: { id: string; at: number },
  nodeId: string,
  now: number,
): boolean => last.id === nodeId && now - last.at < DOUBLE_CLICK_MS;

/**
 * Ob und wie viele Unterlagen an einem Knoten hängen — aus **zwei** Quellen.
 *
 * Die eine steht am Objekt selbst: ein `documents`-Feld führen Asset, Project, Product, Building,
 * Floor und Space (gemessen: 101.441 Produkte haben eines). Die andere steht nur in der
 * Dokumentenanalyse, wo eine Auswertung ein extrahiertes Ding an ein echtes Objekt gebunden hat
 * (`Document.Analysis.boundObjects`) — so hängt der Gewerbemietvertrag an seinem `Lease`.
 *
 * Beide zusammen, aber **getrennt gezählt**: „am Objekt hinterlegt" und „von einer Analyse
 * zugeordnet" sind verschiedene Aussagen, und beim Debuggen will man wissen, welche zutrifft.
 */
export interface DocumentBadge {
  /** Unterlagen an diesem Objekt. */
  total: number;
  /** Davon: Unterlagen, die dem Objekt gehören. */
  primary: number;
  /** Davon: Unterlagen, in denen es nur vorkommt. */
  reference: number;
}

/**
 * Wie viele Unterlagen an einem Knoten hängen.
 *
 * **Eine Quelle, nicht zwei.** Bis die Kante am Objekt selbst stand, musste die zweite Hälfte über
 * `Document.Analysis.boundObjects` nachgeladen werden — eine Aggregation über eine fremde Sammlung,
 * deren Ergebnis über eine zusammengesetzte Zeichenkette wieder den Knoten zugeordnet wurde und die
 * im Misserfolg dasselbe lieferte wie im Erfolg ohne Treffer. Seit der Merge die Kante schreibt,
 * steht alles im Feld, und die Analyse ist wieder das, was sie ist: die Historie der Läufe.
 */
export const documentBadge = (attributes: Record<string, any> | undefined): DocumentBadge => {
  const raw = attributes?.documents;
  const eintraege = Array.isArray(raw) ? raw.filter(Boolean) : [];
  const reference = eintraege.filter(entry => !isPrimaryDocument(entry)).length;
  return { total: eintraege.length, primary: eintraege.length - reference, reference };
};

/**
 * Ob eine Unterlage dem Objekt **gehört** oder es nur **erwähnt**.
 *
 * Der Rollenkatalog trennt das seit jeher: `primaryEntities` sind die Objekte, über die ein
 * Dokument primär spricht, `referenceEntities` die, die darin vorkommen. Der Mietvertrag IST das
 * Dokument des Mietverhältnisses; der Vermieter steht nur darin. Seit der Merge diese Herkunft an
 * die Kante schreibt, lässt sich beides im Bild auseinanderhalten.
 *
 * **Als Erwähnung gilt nur, was ausdrücklich so vermerkt ist.** Ein Eintrag ohne Herkunft ist eine
 * am Objekt hochgeladene Unterlage — die gehört ihm, auch wenn niemand etwas vermerkt hat.
 * Andersherum gelesen (nur was ausdrücklich primär ist) verlöre jeder Altbestand seine Zuordnung.
 */
export const isPrimaryDocument = (entry: any): boolean => {
  if (!entry) return false;
  return typeof entry !== "object" || entry.herkunft !== "referenz";
};

/**
 * Wo auf der Kante ihre Fahne sitzt.
 *
 * Zwischen zwei Knoten liegen oft **mehrere** Kanten: eine beidseitige Beziehung erscheint als Hin-
 * und Rückkante, und zwei verschiedene Relationen können dasselbe Paar verbinden. Alle Fahnen in
 * die Mitte zu setzen legt sie übereinander — man liest dann keine davon.
 *
 * Deshalb bekommt jede Kante eines Paares einen eigenen Anteil der Strecke, symmetrisch um die
 * Mitte. Der Schlüssel ist das **ungeordnete** Paar: Hin- und Rückkante verbinden dieselben zwei
 * Knoten und dürfen sich nicht gegenseitig übersehen.
 */
export const linkLabelPositions = (links: ExplorerLink[]): Record<string, number> => {
  const byPair = new Map<string, ExplorerLink[]>();
  for (const link of links) {
    const source = typeof link.source === "string" ? link.source : String((link.source as any)?.id ?? "");
    const target = typeof link.target === "string" ? link.target : String((link.target as any)?.id ?? "");
    const key = [source, target].sort().join("|");
    const known = byPair.get(key);
    if (known) known.push(link);
    else byPair.set(key, [link]);
  }

  const positions: Record<string, number> = {};
  byPair.forEach(group => {
    group.forEach((link, index) => {
      // Eine Kante sitzt mittig; mehrere verteilen sich um die Mitte, ohne die Enden zu erreichen —
      // dort klebte die Fahne im Knoten.
      const spread = 0.34;
      const offset = group.length === 1 ? 0 : (index / (group.length - 1) - 0.5) * spread;
      positions[link.id] = 0.5 + offset;
    });
  });
  return positions;
};
